import React, { FC, Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { helmetJsonLdProp } from 'react-schemaorg';
import { BreadcrumbList } from 'schema-dts';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  DEFAULT_NEW_STUDIO_CATEGORY_SLUG,
  DEFAULT_STUDIO_CATEGORY_SLUG,
  DESIGNED_TEMPLATES_CATEGORY_SLUG,
  SOCIAL_MEDIA_THUMBNAIL_URL,
  SUPPORTED_LANGUAGES,
} from '../../config';
import { useInitialState } from '../../contexts/InitialStateContext';
import { useLocation } from 'react-router-dom';
import { joinWithSlashes } from '../../utils/joinWithSlashes';
import { useExperiments } from '../../contexts/ExperimentsContext';
import { useParamsWithDefaults } from '../../hooks/useParamsWithDefaults';

export const HtmlHead: FC = () => {
  const { t } = useTranslation();
  const { baseUrl, book } = useInitialState();
  const { is } = useExperiments();
  const { categorySlug } = useParamsWithDefaults();
  const rootUrl = new URL(baseUrl).origin;

  const isNewStudioTP = is('specs.funnel.StudioTemplatesPageNewUI', 'true');
  const isHomePage =
    categorySlug ===
    (isNewStudioTP
      ? DEFAULT_NEW_STUDIO_CATEGORY_SLUG
      : DEFAULT_STUDIO_CATEGORY_SLUG);
  const currentCategory = book.categories.find(
    (category) => category.slug === categorySlug,
  );

  const canonicalLink = joinWithSlashes(baseUrl, useLocation().pathname);
  const defaultCanonicalLink = replaceLanguageInDomain(canonicalLink, 'en');
  let title = t(isNewStudioTP ? 'seo.new.title.home' : 'seo.title.home');
  let description = t(
    isNewStudioTP ? 'seo.description.home' : 'seo.new.description.home',
  );

  if (
    isNewStudioTP &&
    !isHomePage &&
    currentCategory &&
    currentCategory.slug === DESIGNED_TEMPLATES_CATEGORY_SLUG
  ) {
    title = t('seo.new.title.designed');
    description = t('seo.new.description.designed');
  } else if (!isHomePage && currentCategory) {
    const category = currentCategory?.displayName;
    title = t(isNewStudioTP ? 'seo.new.title.category' : 'seo.title.category', {
      category,
    });
    description = t(
      isNewStudioTP
        ? 'seo.new.description.category'
        : 'seo.description.category',
      { category },
    );
  }

  const elements = [
    {
      name: t('breadcrumbs.wixcom'),
      item: rootUrl,
    },
    {
      name: t('breadcrumbs.templates'),
      item: `${rootUrl}/website/templates`,
    },
    {
      name: currentCategory
        ? currentCategory.displayName
        : t('breadcrumbs.home'),
      item: canonicalLink,
    },
  ];

  const breadcrumbsListScriptProps = helmetJsonLdProp<BreadcrumbList>({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: elements.map((params, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      ...params,
    })),
  });

  const favicon = isNewStudioTP ? (
    [
      <link
        rel="icon"
        sizes="192x192"
        href="https://static.wixstatic.com/shapes/ee1397_18a51f58ce2447b083f693dff36b019f.svg"
        type="image/svg+xml"
      />,
      <link
        rel="shortcut icon"
        href="https://static.wixstatic.com/shapes/ee1397_18a51f58ce2447b083f693dff36b019f.svg"
        type="image/svg+xml"
      />,
      <link
        rel="apple-touch-icon"
        href="https://static.wixstatic.com/shapes/ee1397_18a51f58ce2447b083f693dff36b019f.svg"
        type="image/svg+xml"
      />,
    ]
  ) : (
    <link
      type="image/png"
      href="https://static.parastorage.com/services/santa-resources/dist/editor/favicons/wixStudioFavicon.ico"
      rel="shortcut icon"
    />
  );

  const isNoIndex = isNewStudioTP ? !isHomePage : true;

  return (
    <Helmet script={[breadcrumbsListScriptProps]}>
      {isNoIndex && <meta name="robots" content="noindex" />}
      {favicon}
      <title>{title}</title>
      <meta name="description" content={description} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@wix" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SOCIAL_MEDIA_THUMBNAIL_URL} />

      <meta name="og:type" content="website" />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={description} />
      <meta name="og:url" content={canonicalLink} />
      <meta property="og:image" content={SOCIAL_MEDIA_THUMBNAIL_URL} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="628" />

      <link rel="canonical" href={canonicalLink} />
      <link rel="alternate" hrefLang="x-default" href={defaultCanonicalLink} />
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          rel="alternate"
          key={lang}
          hrefLang={lang}
          href={replaceLanguageInDomain(canonicalLink, lang)}
        />
      ))}
    </Helmet>
  );
};

const replaceLanguageInDomain = (url: string, lang: string) => {
  const newUrl = new URL(url);
  const langDomain = lang === 'en' ? 'www' : lang;
  newUrl.hostname = newUrl.hostname.replace(/^\w+\./, `${langDomain}.`);
  return newUrl.toString();
};
